'use client';

/**
 * ResetNotice — declarative inline banner shown when the backend reset the user
 * off a foreign / not-found ?c= onto a fresh conversation.
 *
 * WHY a banner and not a toast: the reset changes conversationId, which triggers
 * a router.replace (URL ?c= swap). A one-shot Sonner toast dispatched during that
 * navigation is reliably dropped by the Next-router × Sonner interaction. A banner
 * driven by a persistent store flag (`resetNotice`) renders declaratively whenever
 * the flag is set, so it survives the navigation. Cleared on the next new/resume
 * conversation or by manual dismiss.
 *
 * Styling follows the warm Art of Living palette (peach-cream surface, navy text,
 * orange accent) rather than a generic blue "info alert", to stay cohesive with the
 * assistant card it sits inside.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Info, X } from 'lucide-react';
import { useConversationStore } from '@/stores/conversation-store';

export function ResetNotice() {
  const show = useConversationStore((s) => s.resetNotice);
  const setResetNotice = useConversationStore((s) => s.setResetNotice);

  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          role="status"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          className="mx-[16px] mb-1 mt-[12px] flex items-center gap-[12px] rounded-2xl border-[1px] border-[#f1e2d1] bg-gradient-to-br from-[#fff7ef] to-[#fdeede] px-3.5 py-2.5 shadow-[0_2px_8px_-3px_rgba(227,96,1,0.12)]"
        >
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-design-orange/10 text-design-orange">
            <Info className="size-[15px]" />
          </span>
          {/* m-0 defeats Bootstrap's global `p { margin-bottom: 1rem }` (loaded
              site-wide) — without it, items-center centers the text's MARGIN box
              and the visible copy sits ~8px too high relative to the icon. */}
          <p className="m-0 flex-1 font-['Work_Sans'] text-[13px] leading-snug text-design-title">
            That conversation isn&apos;t available. Start a new conversation
            below.
          </p>
          <button
            onClick={() => setResetNotice(false)}
            aria-label="Dismiss"
            className="shrink-0 rounded-lg p-1 text-design-caption transition-colors hover:bg-design-orange/10 hover:text-design-orange"
          >
            <X className="size-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
